import React from "react";

import { Spinner, Text } from "@performyard/py-components";

import { CompensationInfoWidget } from "./Widget";

import { pluralize } from "./utils";

type StatusRow = {
	label: string;
	count: number;
	color: string;
};

const colorClassMap: Record<string, string> = {
	"green-5": "bg-green-5",
	"yellow-2": "bg-yellow-2",
	"neutral-2": "bg-neutral-2",
};

export const BudgetStatusWidget = ({
	rows,
	isLoading = false,
	testId = "budget-status-widget",
}: {
	rows: StatusRow[];
	isLoading?: boolean;
	testId?: string;
}) => {
	return (
		<div
			data-testid={testId}
			className="flex max-h-[112px] flex-1 flex-col rounded-md border border-solid border-neutral-2 p-4"
		>
			<Text size="paragraph-3" weight="regular" color="neutral-5">
				Budget Status
			</Text>
			{isLoading ? (
				<div className="flex h-[48px] w-full items-center justify-around">
					<Spinner size={32} />
				</div>
			) : (
				<div className="mt-3 flex flex-col">
					{rows.map((row) => (
						<div key={row.label} className="flex items-center justify-between">
							<div className="flex items-center gap-2">
								<span
									className={`inline-block size-3 rounded-full ${colorClassMap[row.color] ?? ""}`}
								/>
								<Text size="caption" weight="semi-bold">
									{row.label}
								</Text>
							</div>
							<Text size="caption" weight="regular">
								{row.count}
							</Text>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

type CycleBudgetSummaryProps = {
	cycleBudget: number;
	approvedSpend: number;
	percentIncrease: number | null;
	approvedBudgetCount: number;
	submittedBudgetCount: number;
	notSubmittedBudgetCount: number;
	approvedBudgetsPercent: string;
	notApprovedBudgetsPercent: string;
	notApprovedSpend: number;
};

export const CycleBudgetSummary: React.FC<CycleBudgetSummaryProps> = ({
	cycleBudget,
	approvedSpend,
	percentIncrease,
	approvedBudgetCount,
	submittedBudgetCount,
	notSubmittedBudgetCount,
	approvedBudgetsPercent,
	notApprovedBudgetsPercent,
	notApprovedSpend,
}) => {
	const statusRows = [
		{ label: "Approved", count: approvedBudgetCount, color: "green-5" },
		{ label: "Submitted", count: submittedBudgetCount, color: "yellow-2" },
		{ label: "Not Submitted", count: notSubmittedBudgetCount, color: "neutral-2" },
	];
	const totalBudgetsCount = approvedBudgetCount + submittedBudgetCount + notSubmittedBudgetCount;
	const notApprovedBudgetsCount = submittedBudgetCount + notSubmittedBudgetCount;

	return (
		<div className="mt-6 flex w-full gap-4">
			<CompensationInfoWidget
				title="Cycle Budget"
				counter={cycleBudget}
				description={[
					`Represents ${totalBudgetsCount} ${pluralize(totalBudgetsCount, "budget", "budgets")} at ${percentIncrease != null ? parseFloat(percentIncrease.toFixed(2)) : 0}% increase`,
				]}
			/>
			<CompensationInfoWidget
				title="Approved Spend"
				counter={approvedSpend}
				description={[
					`${approvedBudgetCount} ${pluralize(approvedBudgetCount, "budget", "budgets")} approved (${approvedBudgetsPercent}%)`,
				]}
			/>
			<CompensationInfoWidget
				title="Not Approved"
				counter={notApprovedSpend}
				description={[
					`${notApprovedBudgetsCount} ${pluralize(notApprovedBudgetsCount, "budget", "budgets")} not approved (${notApprovedBudgetsPercent}%)`,
				]}
			/>
			<BudgetStatusWidget rows={statusRows} />
		</div>
	);
};
